import React, { useState, useEffect, useRef } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Share2, X, Menu, ChevronRight, ChevronDown, PanelTopClose, Calculator, Sparkles } from "lucide-react";

// Assets
import Logo from "../../../assets/logo.png";
import LogoRounded from "../../../assets/logo-round.png";
import Linkedin from "../../../assets/logo/linkedin.webp";
import Instagram from "../../../assets/logo/instagram.webp";
import Facebook from "../../../assets/logo/facebook.webp";
import Youtube from "../../../assets/logo/youtube.webp";
import Gmail from "../../../assets/logo/gmail.png";

import LoginForm from "../../Frontend/Membership/Login";
import SignupForm from "../../Frontend/Membership/Signup";
import "./Nav.css";

const Nav = () => {
  const location = useLocation();

  /* =========================
      STATES
  ========================== */
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [activeSubMenus, setActiveSubMenus] = useState({});
  const [showQuickAccess, setShowQuickAccess] = useState(false); // desktop only
  const [showCalculatorMenu, setShowCalculatorMenu] = useState(false);
  const [authView, setAuthView] = useState("login");
  const [showSocials, setShowSocials] = useState(false);

  const showNavbar = true;

  // Mobile
  // Mobile
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileExpandedTab, setMobileExpandedTab] = useState(null);
  const [mobileExpandedItem, setMobileExpandedItem] = useState(null);

  const quickAccessRef = useRef(null);
  const calculatorRef = useRef(null);
  const socialRef = useRef(null);


  const [showAuthModal, setShowAuthModal] = useState(false); // mobile auth modal

  /* =========================
      KEEP NAV VISIBLE
  ========================== */
  useEffect(() => {
    const handleOpenQuickAccess = (e) => {
      const view = e.detail?.view || "login"; // default to login
      setAuthView(view);                     // set login/signup view

      // Check if mobile or desktop
      const isMobile = window.innerWidth <= 992;

      if (isMobile) {
        setShowAuthModal(true);  // open mobile auth modal
      } else {
        setShowQuickAccess(true); // open desktop Quick Access panel
      }
    };

    window.addEventListener("OPEN_QUICK_ACCESS", handleOpenQuickAccess);

    return () => {
      window.removeEventListener("OPEN_QUICK_ACCESS", handleOpenQuickAccess);
    };
  }, []);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      const target = event.target;
      const path = typeof event.composedPath === "function" ? event.composedPath() : [];
      const isInside = (ref) => {
        if (!ref.current) return false;
        return ref.current.contains(target) || path.includes(ref.current);
      };

      if (!isInside(quickAccessRef)) {
        setShowQuickAccess(false);
      }
      if (!isInside(calculatorRef)) {
        setShowCalculatorMenu(false);
      }
      if (!isInside(socialRef)) {
        setShowSocials(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  /* =========================
      CLOSE MENUS ON ROUTE CHANGE
  ========================== */
  useEffect(() => {
    setActiveDropdown(null);
    setActiveSubMenus({});
    setShowQuickAccess(false);
    setShowSocials(false);
    setMobileMenuOpen(false);
    setMobileExpandedTab(null);
    setMobileExpandedItem(null);
    setShowAuthModal(false);
  }, [location]);

  /* =========================
      NAV TABS
  ========================== */
  const tabs = [
    { id: "home", label: "Home", path: "/" },
    // { id: "dashboard", label: "Dashboard", path: "/dashboard" },

    {
      id: "about_us",
      label: "About Us",
      path: "/about_us",
      dropdown: [
        {
          label: "Leadership Team",
          dropdown: [
            { label: "Founder", path: "/our_story/teamlead?tab=founder" },
            { label: "Co-Founder", path: "/our_story/teamlead?tab=co-founder" },
            { label: "Board of Mentors", path: "/our_story/teamlead?tab=mentors" },
            { label: "Board of Advisors", path: "/our_story/teamlead?tab=advisors" },
            { label: "Board of Ambassadors", path: "/our_story/teamlead?tab=ambassadors" },]
        },
        { label: "Our Story", path: "/about_us#our-story" },
        { label: "Why IHN", path: "/about_us#why-ihn" },
        { label: "Our Vision", path: "/our_story/vision?visionTab=vision" },
        { label: "Our Mission", path: "/our_story/vision?visionTab=mission" },
        { label: "Our Code", path: "/our_story/vision?visionTab=code" },


      ]
    },
    {
      id: "events",
      label: "Events",
      path: "/events",
      dropdown: [
        { label: "Network Multiplier", path: "/events/network_multiplier" },
        {
          label: "Konnect",
          dropdown: [
            { label: "Konnect 2023", path: "/events/konnect/konnect2023" },
            { label: "Konnect 2022", path: "/events/konnect/konnect2022" }
          ]
        },
        { label: "Socials", path: "/events/socials" },
        { label: "OutReach", path: "/events/outreach" }
      ]
    },
    {
      id: "media",
      label: "Media",
      path: "/media",
      dropdown: [
        { label: "News", path: "/media/news" },
        { label: "Blog", path: "/media/blog" },
        { label: "Gallery", path: "/media/gallery" },
        { label: "Stories", path: "/media/stories" },

      ]
    },
    { id: "contact", label: "Contact", path: "/contact" }
  ];



  /* =========================
      DESKTOP DROPDOWN RENDER
  ========================== */
  const renderDropdown = (items, level = 0) => (
    <div className="dropdown-box">
      {items.map((item, i) => {
        const hasChildren = item.dropdown;
        const isOpen = activeSubMenus[level] === item.label;

        return (
          <div
            key={i}
            className="dropdown-item-wrapper"
            onMouseEnter={() => hasChildren && setActiveSubMenus(p => ({ ...p, [level]: item.label }))}
            onMouseLeave={() => hasChildren && setActiveSubMenus(p => ({ ...p, [level]: null }))}
          >
            {item.path ? (
              <NavLink to={item.path} className="dropdown-item">{item.label}</NavLink>
            ) : (
              <div
                className="dropdown-item has-children"
                onClick={() => hasChildren && setActiveSubMenus(p => ({ ...p, [level]: isOpen ? null : item.label }))}
                style={{ cursor: 'pointer' }}
              >
                {item.label} <ChevronRight size={14} />
              </div>
            )}

            <AnimatePresence>
              {hasChildren && isOpen && (
                <motion.div
                  initial={{ opacity: 0, x: 10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 10 }}
                  className="dropdown-submenu"
                >
                  {renderDropdown(item.dropdown, level + 1)}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </div>
  );
  const renderMobileDropdown = (items, level = 0) => (
    <div className={`mobile-level mobile-level-${level}`}>
      {items.map((item) => {
        const key = `${level}-${item.label}`;
        const isExpanded = mobileExpandedItem === key;


        return (
          <div key={key} className="mobile-item">
            <div className="mobile-row">
              {item.path ? (
                <NavLink
                  to={item.path}
                  className="mobile-sub-link"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {item.label}
                </NavLink>
              ) : (
                <span className="mobile-sub-label">{item.label}</span>
              )}

              {item.dropdown && (
                <button
                  className="mobile-expand-btn"
                  onClick={() => {
                    if (!isExpanded) {
                      setMobileExpandedItem(key);

                    }
                  }}

                >
                  <ChevronDown size={18} />
                </button>
              )}
            </div>

            <AnimatePresence>
              {item.dropdown && isExpanded && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                >
                  {renderMobileDropdown(item.dropdown, level + 1)}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </div>
  );


  return (
    <>
      {/* ================= NAVBAR ================= */}
      <AnimatePresence>
        {showNavbar && (
          <motion.nav
            initial={{ y: -100, x: "-50%" }}
            animate={{ y: 0, x: "-50%" }}
            exit={{ y: -100, x: "-50%" }}
            className="navbar-pill"
          >
            <div className="nav-container">
              {/* LOGO */}
              <div className="logo-combo">
                <img src={LogoRounded} className="logo-rotating" alt="Logo" />
                <Link to="/"> <img src={Logo} className="main-logo" alt="Main Logo" /></Link>
              </div>

              {/* DESKTOP NAV */}
              <ul className="nav-links desktop-only">
                {tabs.map(tab => (
                  <li
                    key={tab.id}
                    className="nav-li"
                    onMouseEnter={() => setActiveDropdown(tab.id)}
                    onMouseLeave={() => setActiveDropdown(null)}
                  >
                    <NavLink to={tab.path} className="nav-item">{tab.label}</NavLink>
                    {tab.dropdown && activeDropdown === tab.id && (
                      <div className="dropdown-anchor">
                        {renderDropdown(tab.dropdown)}
                      </div>
                    )}
                  </li>
                ))}
              </ul>

              {/* ACTIONS */}
              <div className="nav-actions">
                {/* DESKTOP QUICK ACCESS */}
                <div
                  className="quick-access-wrapper desktop-only"
                  ref={quickAccessRef}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <button
                    className="quick-access-btn"
                    onClick={() => {
                      setShowCalculatorMenu(false);
                      setShowSocials(false);
                      setShowQuickAccess((p) => !p);
                    }}
                  >
                    Quick Access
                  </button>

                  {showQuickAccess && (
                    <div className="quick-access-panel">
                      <div className="auth-switch">
                        <button
                          className={authView === "login" ? "active" : ""}
                          onClick={() => setAuthView("login")}
                        >
                          Login
                        </button>
                        <button
                          className={authView === "signup" ? "active" : ""}
                          onClick={() => setAuthView("signup")}
                        >
                          Sign Up
                        </button>
                      </div>

                      {authView === "login" ? <LoginForm /> : <SignupForm />}
                    </div>
                  )}
                </div>
        
                {/* HAMBURGER */}
                <button className="hamburger-btn" onClick={() => setMobileMenuOpen(p => !p)}>
                  {mobileMenuOpen ? <PanelTopClose size={22} /> : <Menu size={22} />}
                </button>
                {/* SOCIALS */}
                <div
                  className="social-box"
                  ref={socialRef}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <button className="share-btn" onClick={() => setShowSocials(p => !p)}>
                    {showSocials ? <X size={18} /> : <Share2 size={18} />}
                  </button>

                  {showSocials && (
                    <div className="social-flyout">
                      <a href="https://www.linkedin.com/company/indianhospitalitynetwork/" target="_blank" rel="noreferrer">
                        <img src={Linkedin} alt="linkedin-logo" className="social-logo" />
                      </a>
                      <a href="https://www.instagram.com/indianhospitalitynetwork/" target="_blank" rel="noreferrer">
                        <img src={Instagram} alt="insta-logo" className="social-logo" />
                      </a>
                      <a href="https://www.facebook.com/indianhospitalitynetwork/" target="_blank" rel="noreferrer">
                        <img src={Facebook} alt="facebook-logo" className="social-logo" />
                      </a>
                      <a href="https://www.youtube.com/@ihntv-li7bc" target="_blank" rel="noreferrer">
                        <img src={Youtube} alt="youtube-logo" className="social-logo" />
                      </a>
                      <a
                        href="mailto:ihnexperience@gmail.com"
                        onClick={(e) => {
                          setTimeout(() => {
                            window.open(
                              "https://mail.google.com/mail/?view=cm&fs=1&to=ihnexperience@gmail.com",
                              "_blank"
                            );
                          }, 500);
                        }}
                      >
                        <img src={Gmail} alt="gmail-logo" className="social-logo" />
                      </a>
                    </div>
                  )}
                </div>


              </div>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>

      {/* ================= MOBILE MENU ================= */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            className="mobile-overlay"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <div className="mobile-menu-container">
              {tabs.map(tab => (
                <div key={tab.id} className="mobile-nav-item-wrapper">
                  <div className="mobile-row">
                    <NavLink
                      to={tab.path}
                      className="mobile-main-link"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {tab.label}
                    </NavLink>

                    {tab.dropdown && (
                      <button
                        className={`mobile-expand-btn ${mobileExpandedTab === tab.id ? "active" : ""}`}
                        onClick={() => {
                          setMobileExpandedTab(
                            mobileExpandedTab === tab.id ? null : tab.id
                          );
                          setMobileExpandedItem(null); // reset nested dropdowns
                        }}
                      >
                        <ChevronDown size={20} />
                      </button>
                    )}

                  </div>

                  <AnimatePresence>
                    {tab.dropdown && mobileExpandedTab === tab.id && (
                      <motion.div
                        className="mobile-submenu-box"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                      >
                        {renderMobileDropdown(tab.dropdown)}

                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}

              <div className="mobile-footer">
                <button
                  className="mobile-quick-access-btn"
                  onClick={() => {
                    setMobileMenuOpen(false);
                    setAuthView("login");
                    setShowAuthModal(true);
                  }}
                >
                  Quick Access
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ================= MOBILE AUTH MODAL ================= */}
      <AnimatePresence>
        {showAuthModal && (
          <motion.div
            className="mobile-auth-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="mobile-auth-modal"
              initial={{ y: 40, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 40, opacity: 0 }}
            >
              <button
                className="auth-close-btn"
                onClick={() => setShowAuthModal(false)}
              >
                <X size={22} />
              </button>

              <div className="auth-switch">
                <button
                  className={authView === "login" ? "active" : ""}
                  onClick={() => setAuthView("login")}
                >
                  Login
                </button>
                <button
                  className={authView === "signup" ? "active" : ""}
                  onClick={() => setAuthView("signup")}
                >
                  Sign Up
                </button>
              </div>

              {authView === "login" ? <LoginForm /> : <SignupForm />}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Nav;
